<template>
  <b-modal 
    id="modal-add-course" 
    centered 
    hide-footer
    :title="editMode ? 'Edit Course' : 'Add Course'"
    size="xl"
    body-class="p-0"
    @hidden="onModalHidden">
    <div class="modal-container">
      <!-- Body -->
      <div class="modal-body-container">
        <div class="row g-0">
          <!-- Left Sidebar - Stepper -->
          <div class="col-md-3 stepper-sidebar border-end" style="background-color: #FAFAFA;">
            <div class="p-4">
              <!-- Step 1 -->
              <div class="step-item" @click="toStep(1)" style="cursor: pointer;" :class="{ 'completed': currentStep > 1, 'active': currentStep === 1 }">
                <div class="step-indicator">
                  <div class="step-number">
                    <i v-if="currentStep > 1" class="fe fe-check"></i>
                    <span v-else>1</span>
                  </div>
                  <div class="step-line"></div>
                </div>
                <div class="step-content">
                  <div class="step-label">Step 1</div>
                  <div class="step-title">Competency/Matrix KPLH</div>
                  <div class="step-status" v-if="currentStep > 1">Completed</div>
                  <div class="step-status" v-else-if="currentStep === 1">In Progress</div>
                </div>
              </div>

              <!-- Step 2 -->
              <div class="step-item" @click="toStep(2)" style="cursor: pointer;" :class="{ 'completed': currentStep > 2, 'active': currentStep === 2 }">
                <div class="step-indicator">
                  <div class="step-number">
                    <i v-if="currentStep > 2" class="fe fe-check"></i>
                    <span v-else>2</span>
                  </div>
                  <div class="step-line"></div>
                </div>
                <div class="step-content">
                  <div class="step-label">Step 2</div>
                  <div class="step-title">Course Details</div>
                  <div class="step-status" v-if="currentStep > 2">Completed</div>
                  <div class="step-status" v-else-if="currentStep === 2">In Progress</div>
                </div>
              </div>

              <!-- Step 3 -->
              <div class="step-item" @click="toStep(3)" style="cursor: pointer;" :class="{ 'completed': currentStep > 3, 'active': currentStep === 3 }">
                <div class="step-indicator">
                  <div class="step-number">
                    <i v-if="currentStep > 3" class="fe fe-check"></i>
                    <span v-else>3</span>
                  </div>
                  <div class="step-line"></div>
                </div>
                <div class="step-content">
                  <div class="step-label">Step 3</div>
                  <div class="step-title">Platform Details</div>
                  <div class="step-status" v-if="currentStep > 3">Completed</div>
                  <div class="step-status" v-else-if="currentStep === 3">In Progress</div>
                </div>
              </div>

              <!-- Step 4 -->
              <div class="step-item" @click="toStep(4)" style="cursor: pointer;" :class="{ 'completed': currentStep > 4, 'active': currentStep === 4 }">
                <div class="step-indicator">
                  <div class="step-number">
                    <i v-if="currentStep > 4" class="fe fe-check"></i>
                    <span v-else>4</span>
                  </div>
                  <div class="step-line"></div>
                </div>
                <div class="step-content">
                  <div class="step-label">Step 4</div>
                  <div class="step-title">Course Scope</div>
                  <div class="step-status" v-if="currentStep > 4">Completed</div>
                  <div class="step-status" v-else-if="currentStep === 4">In Progress</div>
                </div>
              </div>

              <!-- Step 5 - Hidden in edit mode -->
              <div v-if="!editMode" class="step-item" @click="toStep(5)" style="cursor: pointer;" :class="{ 'completed': currentStep > 5, 'active': currentStep === 5 }">
                <div class="step-indicator">
                  <div class="step-number">
                    <i v-if="currentStep > 5" class="fe fe-check"></i>
                    <span v-else>5</span>
                  </div>
                </div>
                <div class="step-content">
                  <div class="step-label">Step 5</div>
                  <div class="step-title">Event/Batch</div>
                  <div class="step-status" v-if="currentStep > 5">Completed</div>
                  <div class="step-status" v-else-if="currentStep === 5">In Progress</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Right Content Area -->
          <div class="col-md-9">
            <div class="p-4">
              <!-- Back Button -->
              <button 
                v-if="currentStep > 1" 
                class="btn btn-danger mb-4"
                @click="previousStep">
                <i class="fe fe-arrow-left me-2"></i>
                Back
              </button>

              <!-- Step 1: Competency/Matrix KPLH -->
              <div v-if="currentStep === 1">
                <h4 class="mb-4 font-weight-bold">Competency/Matrix KPLH</h4>

                <div class="row">
                  <!-- Type (Internal/External/Thematic) -->
                  <div class="col-md-12 mb-3">
                    <label class="form-label">
                      Type (Internal/External/Thematic)<span class="text-danger">*</span>
                    </label>
                    <v-select
                      v-model="formData.competency_type"
                      :options="typeOptions"
                      placeholder="Select Type Competency"
                      v-validate="'required'"
                      name="competency_type"
                      :class="{ 'is-invalid': errors.has('competency_type') }">
                    </v-select>
                    <span class="text-danger small" v-if="errors.has('competency_type')">
                      {{ errors.first('competency_type') }}
                    </span>
                  </div>

                  <!-- Role -->
                  <div class="col-md-6 mb-3">
                    <label class="form-label">
                      Role<span v-if="isRoleRequired" class="text-danger">*</span>
                    </label>
                    <v-select
                      v-model="formData.role"
                      :options="roleOptions"
                      label="label"
                      :reduce="option => option.value"
                      placeholder="Select Role Competency"
                      :loading="isLoadingRoles"
                      v-validate="isRoleRequired ? 'required' : ''"
                      name="role"
                      :class="{ 'is-invalid': errors.has('role') }">
                      <template #option="option">
                        <div>
                          <div>{{ option.label }}</div>
                          <small v-if="option.code" class="text-muted">Code: {{ option.code }}</small>
                        </div>
                      </template>
                      <template #no-options>
                        <span v-if="isLoadingRoles">Loading...</span>
                        <span v-else>No role available</span>
                      </template>
                    </v-select>
                    <span class="text-danger small" v-if="errors.has('role')">
                      {{ errors.first('role') }}
                    </span>
                  </div>

                  <!-- Matrix KPLH -->
                  <div class="col-md-6 mb-3">
                    <label class="form-label">
                      KPLH / Non KPLH<span v-if="isMatrixKPLHRequired" class="text-danger">*</span>
                    </label>
                    <v-select
                      v-model="formData.matrixKPLH"
                      :options="matrixKPLHOptions"
                      label="label"
                      :reduce="option => option.value"
                      placeholder="Select Matrix KPLH"
                      v-validate="isMatrixKPLHRequired ? 'required' : ''"
                      name="matrix_kplh"
                      :class="{ 'is-invalid': errors.has('matrix_kplh') }"
                      :clearable="true">
                    </v-select>
                    <span class="text-danger small" v-if="errors.has('matrix_kplh')">
                      {{ errors.first('matrix_kplh') }}
                    </span>
                  </div>
                </div>

                <!-- Competency & Proficiency Pairs -->
                <div class="d-flex justify-content-between align-items-center mb-3 mt-4">
                  <h5 class="mb-0 font-weight-bold">
                    Competencies
                  </h5>
                  <button class="btn btn-danger btn-sm" @click="addCompetency">
                    <i class="fe fe-plus me-1"></i>
                    Add Competency
                  </button>
                </div>

                <!-- Competency List -->
                <div v-for="(competency, index) in formData.competencies" :key="index" class="mb-3">
                  <div class="card">
                    <div class="card-body">
                      <div class="d-flex justify-content-between align-items-center mb-3">
                        <h6 class="mb-0 font-weight-bold">Competency {{ index + 1 }}</h6>
                        <button 
                          v-if="formData.competencies.length > 1"
                          class="btn btn-sm btn-outline-danger"
                          @click="removeCompetency(index)">
                          <i class="fe fe-trash-2"></i>
                        </button>
                      </div>

                      <div class="row">
                        <!-- Competency -->
                        <div class="col-md-8 mb-3">
                          <label class="form-label">
                            Competency
                          </label>
                          <v-select
                            v-model="competency.competency"
                            :options="competencyOptions"
                            label="label"
                            :reduce="option => option.value"
                            placeholder="Select Competency"
                            :loading="isLoadingCompetencies"
                            @input="onCompetencyChange(index)"
                            :name="`competency_${index}`"
                            :class="{ 'is-invalid': errors.has(`competency_${index}`) }">
                            <template #option="option">
                              <div>
                                <div>{{ option.label }}</div>
                                <small v-if="option.code" class="text-muted">Code: {{ option.code }}</small>
                              </div>
                            </template>
                            <template #no-options>
                              <span v-if="isLoadingCompetencies">Loading...</span>
                              <span v-else>No competency available</span>
                            </template>
                          </v-select>
                          <span class="text-danger small" v-if="errors.has(`competency_${index}`)">
                            {{ errors.first(`competency_${index}`) }}
                          </span>
                        </div>

                        <!-- Proficiency Level -->
                        <div class="col-md-4 mb-3">
                          <label class="form-label">
                            Proficiency Level<span class="text-danger">*</span>
                          </label>
                          <div class="d-flex gap-2">
                            <button
                              v-for="level in 5"
                              :key="level"
                              type="button"
                              class="btn btn-proficiency"
                              :class="{ 'active': competency.proficiency === level }"
                              @click="selectProficiency(index, level)">
                              {{ level }}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Step 2: Course Details -->
              <div v-if="currentStep === 2">
                <h4 class="mb-4 font-weight-bold">Course Details</h4>

                <div class="row">
                  <!-- Course Name -->
                  <div class="col-md-6 mb-3">
                    <label class="form-label">
                      Course Name<span class="text-danger">*</span>
                    </label>
                    <input 
                      type="text" 
                      class="form-control" 
                      placeholder="Course Name"
                      v-model="formData.courseName"
                      v-validate="'required'"
                      name="course_name"
                      :class="{ 'is-invalid': errors.has('course_name') }">
                    <span class="text-danger small" v-if="errors.has('course_name')">
                      {{ errors.first('course_name') }}
                    </span>
                  </div>

                  <!-- Course -->
                  <div class="col-md-6 mb-3">
                    <label class="form-label">
                      Course Number
                    </label>
                    <input 
                      type="text" 
                      class="form-control" 
                      placeholder="Input Course Number"
                      v-model="formData.noCourse">
                  </div>

                  <!-- Course Type -->
                  <div class="col-md-6 mb-3">
                    <label class="form-label">
                      Course Type<span class="text-danger">*</span>
                    </label>
                    <v-select
                      v-model="formData.courseType"
                      :options="courseTypeOptions"
                      label="label"
                      :reduce="option => option.value"
                      placeholder="Select Course Type"
                      :loading="isLoadingCourseTypes"
                      v-validate="'required'"
                      name="course_type"
                      :class="{ 'is-invalid': errors.has('course_type') }"
                      >
                      <template #option="option">
                        <div>
                          <div>{{ option.label }}</div>
                          <small v-if="option.code" class="text-muted">Code: {{ option.code }}</small>
                        </div>
                      </template>
                      <template #no-options>
                        <span v-if="isLoadingCourseTypes">Loading...</span>
                        <span v-else>No course type available</span>
                      </template>
                    </v-select>
                    <span class="text-danger small" v-if="errors.has('course_type')">
                      {{ errors.first('course_type') }}
                    </span>
                  </div>

                  <!-- Category -->
                  <div class="col-md-6 mb-3">
                    <label class="form-label">
                      Category
                    </label>
                    <v-select
                      v-model="formData.category"
                      :options="categoryOptions"
                      label="label"
                      :reduce="option => option.value"
                      placeholder="Select Category"
                      :loading="isLoadingCategories"
                      name="category"
                      :class="{ 'is-invalid': errors.has('category') }"
                      >
                      <template #option="option">
                        <div>
                          <div>{{ option.label }}</div>
                          <small v-if="option.code" class="text-muted">Code: {{ option.code }}</small>
                        </div>
                      </template>
                      <template #no-options>
                        <span v-if="isLoadingCategories">Loading...</span>
                        <span v-else>No category available</span>
                      </template>
                    </v-select>
                    <span class="text-danger small" v-if="errors.has('category')">
                      {{ errors.first('category') }}
                    </span>
                  </div>

                  <!-- STEM Category -->
                  <div class="col-md-6 mb-3">
                    <label class="form-label">
                      STEM Category<span class="text-danger">*</span>
                    </label>
                    <div class="row">
                      <div class="col-md-4">
                        <!-- Main Category: STEM / Non STEM -->
                        <v-select
                          v-model="formData.stemCategoryMain"
                          :options="stemMainOptions"
                          placeholder="Select Main"
                          v-validate="'required'"
                          name="stem_main"
                          :class="{ 'is-invalid': errors.has('stem_main') }"
                          @input="onStemMainChange"
                          class="flex-fill">
                        </v-select>
                      </div>
                      <div class="col-md-8">
                        <!-- Sub Category: Based on main selection -->
                        <v-select
                          v-model="formData.stemCategorySub"
                          :options="currentStemSubOptions"
                          placeholder="Select Sub"
                          v-validate="'required'"
                          name="stem_sub"
                          :class="{ 'is-invalid': errors.has('stem_sub') }"
                          :disabled="!formData.stemCategoryMain"
                          class="flex-fill">
                        </v-select>
                      </div>
                    </div>
                    <span class="text-danger small" v-if="errors.has('stem_main') || errors.has('stem_sub')">
                      STEM Category is required
                    </span>
                  </div>

                  <!-- Learning Hours -->
                  <div class="col-md-6 mb-3">
                    <label class="form-label">
                      Learning Hours<span class="text-danger">*</span>
                    </label>
                    <input 
                      type="number" 
                      class="form-control" 
                      placeholder="0"
                      v-model="formData.learningHours"
                      v-validate="'required|min_value:1'"
                      name="learning_hours"
                      :class="{ 'is-invalid': errors.has('learning_hours') }">
                    <span class="text-danger small" v-if="errors.has('learning_hours')">
                      {{ errors.first('learning_hours') }}
                    </span>
                  </div>

                  <!-- Course URL -->
                  <div class="col-md-6 mb-3">
                    <label class="form-label">
                      Course URL
                    </label>
                    <input 
                      type="url" 
                      class="form-control" 
                      placeholder="Website"
                      v-model="formData.courseUrl"
                      maxlength="50"
                      :class="{ 'is-invalid': formData.courseUrl && formData.courseUrl.length > 50 }">
                    <div class="d-flex justify-content-between mt-1">
                      <span class="text-danger small" v-if="formData.courseUrl && formData.courseUrl.length > 50">
                        Course URL maksimal 50 karakter
                      </span>
                      <span class="text-muted small ms-auto">
                        {{ formData.courseUrl ? formData.courseUrl.length : 0 }}/50
                      </span>
                    </div>
                  </div>

                  <!-- Certificate Validity Period -->
                  <div class="col-md-6 mb-3">
                    <label class="form-label">
                      Certificate Validity Period
                    </label>
                    <div class="row">
                      <div class="col-md-6">
                        <label class="form-label">Year (0-5)</label>
                        <v-select
                          v-model="formData.certificateValidityYear"
                          :options="yearOptions"
                          label="label"
                          :reduce="option => option.value"
                          placeholder="Select Year"
                          @input="onValidityYearChange"
                          >
                        </v-select>
                      </div>
                      <div class="col-md-6">
                        <label class="form-label">Month (0-12)</label>
                        <v-select
                          v-model="formData.certificateValidityMonth"
                          :options="currentMonthOptions"
                          label="label"
                          :reduce="option => option.value"
                          placeholder="Select Month"
                          :disabled="isMonthDisabled"
                          @input="onValidityMonthChange"
                          >
                        </v-select>
                      </div>
                    </div>
                    <small class="text-muted mt-2 d-block">
                      Maximum: 5 years. Total selected: <strong>{{ totalValidityMonths }} months</strong>
                    </small>
                  </div>

                  <!-- Description -->
                  <div class="col-md-6 mb-3">
                    <label class="form-label">
                      Description<span class="text-danger">*</span>
                    </label>
                    <textarea 
                      class="form-control" 
                      rows="4" 
                      placeholder="Input Description"
                      v-model="formData.description"
                      v-validate="'required'"
                      name="description"
                      :class="{ 'is-invalid': errors.has('description') }"
                      maxlength="300"></textarea>
                    <div class="d-flex justify-content-between mt-1">
                      <span class="text-danger small" v-if="errors.has('description')">
                        {{ errors.first('description') }}
                      </span>
                      <span class="text-muted small">
                        {{ formData.description ? formData.description.length : 0 }}/300
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Service Bond -->
                <div class="row mb-3">
                    <div class="col-md-6 mb-3">
                    <b-form-checkbox
                      v-model="formData.isServiceBond"
                      name="service_bond"
                    >
                      Service Bond (Ikatan Dinas)
                    </b-form-checkbox>
                  </div>
                  </div>
                <div class="row">
                  <!-- Course Cover -->
                  <div class="col-md-6 mb-3">
                    <label class="form-label">
                      Course Cover
                    </label>
                    <div 
                      class="upload-area text-center p-4 border rounded"
                      @dragover.prevent="onDragOver"
                      @dragleave.prevent="onDragLeave"
                      @drop.prevent="onDropCourseCover"
                      :class="{ 'drag-active': isDraggingCourseCover }">
                      
                      <!-- Show preview if image is uploaded -->
                      <div v-if="courseCoverPreview" class="position-relative">
                        <img 
                          :src="courseCoverPreview" 
                          alt="Course Cover Preview" 
                          class="img-fluid rounded mb-2"
                          style="max-height: 200px; object-fit: cover;">
                        <div class="mt-2">
                          <button 
                            type="button"
                            class="btn btn-sm btn-outline-danger me-2" 
                            @click="removeCourseCover">
                            <i class="fe fe-trash-2 me-1"></i>
                            Remove
                          </button>
                          <button 
                            type="button"
                            class="btn btn-sm btn-danger" 
                            @click="$refs.courseCoverInput.click()">
                            <i class="fe fe-refresh-cw me-1"></i>
                            Change
                          </button>
                        </div>
                      </div>

                      <!-- Show upload area if no image -->
                      <div v-else>
                        <i class="fe fe-upload-cloud mb-2" style="font-size: 2rem; color: #6c757d;"></i>
                        <p class="mb-2 font-weight-bold">Drag and Drop the Image</p>
                        <p class="text-muted small mb-2">Upload file .jpeg/.jpg/.png (Max. size 800Kb)</p>
                        <p class="text-muted small mb-3">(Image recommendation 1920 x 1080)</p>
                        <button 
                          type="button"
                          class="btn btn-sm btn-danger"
                          @click="$refs.courseCoverInput.click()">
                          Upload
                        </button>
                      </div>
                    </div>
                    
                    <!-- Hidden file input -->
                    <input 
                      ref="courseCoverInput"
                      type="file" 
                      accept="image/jpeg,image/jpg,image/png"
                      style="display: none"
                      @change="onCourseCoverChange">
                  </div>
                </div>
              </div>
              <!-- Step 3: Platform Details -->
              <div v-if="currentStep === 3">
                <h4 class="mb-4 font-weight-bold">Platform Details</h4>

                <div class="row">
                  <!-- Platform -->
                  <div class="col-md-6 mb-3">
                    <label class="form-label">
                      Platform<span class="text-danger">*</span> 
                      <span v-if="formData.platform == 'Ugems University'" class="badge badge-pill ms-2 badge-info">Integrated with Moodle</span>
                    </label>
                    <v-select
                      v-model="formData.platform"
                      :options="platformOptions"
                      label="label"
                      :reduce="option => option.value"
                      placeholder="Select Platform"
                      v-validate="'required'"
                      name="platform"
                      :class="{ 'is-invalid': errors.has('platform') }"
                      >
                    </v-select>
                    <span class="text-danger small" v-if="errors.has('platform')">
                      {{ errors.first('platform') }}
                    </span>
                  </div>

                  <!-- Provider Name (LSP) -->
                  <div class="col-md-6 mb-3">
                    <label class="form-label">
                      Provider Name (LSP)<span class="text-danger">*</span>
                    </label>
                    <v-select
                      v-model="formData.providerName"
                      :options="lspOptions"
                      label="label"
                      :reduce="option => option.value"
                      placeholder="Select Provider Name (LSP)"
                      :loading="isLoadingLSP"
                      v-validate="'required'"
                      name="provider_name"
                      :class="{ 'is-invalid': errors.has('provider_name') }"
                      >
                      <template #option="option">
                        <div>{{ option.label }}</div>
                        <small v-if="option.code" class="text-muted">Code: {{ option.code }}</small>
                      </template>
                    </v-select>
                    <span class="text-danger small" v-if="errors.has('provider_name')">
                      {{ errors.first('provider_name') }}
                    </span>
                  </div>

                  <!-- Training Institution (LDP) -->
                  <div class="col-md-12 mb-3">
                    <label class="form-label">
                      Training Institution (LDP)<span class="text-danger">*</span>
                    </label>
                    <v-select
                      v-model="formData.trainingInstitution"
                      :options="trainingProviderOptions"
                      label="label"
                      :reduce="option => option.value"
                      placeholder="Select Training Institution (LDP)"
                      :loading="isLoadingTrainingProvider"
                      v-validate="'required'"
                      name="training_institution"
                      :class="{ 'is-invalid': errors.has('training_institution') }"
                      @input="onLDPChange"
                      >
                      <template #option="option">
                        <div>{{ option.label }}</div>
                        <small v-if="option.code" class="text-muted">Code: {{ option.code }}</small>
                      </template>
                    </v-select>
                    <span class="text-danger small" v-if="errors.has('training_institution')">
                      {{ errors.first('training_institution') }}
                    </span>
                  </div>

                  <!-- LDP "Other" Fields - Show only when "Other" is selected -->
                  <template v-if="isLDPOther">
                    <!-- Name -->
                    <div class="col-md-6 mb-3">
                      <label class="form-label">
                        Name<span class="text-danger">*</span>
                      </label>
                      <input 
                        type="text" 
                        class="form-control" 
                        placeholder="Provider Name"
                        v-model="formData.ldpOtherName"
                        v-validate="isLDPOther ? 'required' : ''"
                        name="ldp_name"
                        :class="{ 'is-invalid': errors.has('ldp_name') }">
                      <span class="text-danger small" v-if="errors.has('ldp_name')">
                        {{ errors.first('ldp_name') }}
                      </span>
                    </div>

                    <!-- Address -->
                    <div class="col-md-6 mb-3">
                      <label class="form-label">
                        Address<span class="text-danger">*</span>
                      </label>
                      <input 
                        type="text" 
                        class="form-control" 
                        placeholder="Address"
                        v-model="formData.ldpOtherAddress"
                        v-validate="isLDPOther ? 'required' : ''"
                        name="ldp_address"
                        :class="{ 'is-invalid': errors.has('ldp_address') }">
                      <span class="text-danger small" v-if="errors.has('ldp_address')">
                        {{ errors.first('ldp_address') }}
                      </span>
                    </div>

                    <!-- Provider Website -->
                    <div class="col-md-6 mb-3">
                      <label class="form-label">
                        Provider Website
                      </label>
                      <input 
                        type="url" 
                        class="form-control" 
                        placeholder="https://example.com"
                        v-model="formData.ldpOtherWebsite">
                    </div>

                    <!-- Contact Person -->
                    <div class="col-md-6 mb-3">
                      <label class="form-label">
                        Contact Person<span class="text-danger">*</span>
                      </label>
                      <input 
                        type="text" 
                        class="form-control" 
                        placeholder="Contact Person"
                        v-model="formData.ldpOtherContactPerson"
                        v-validate="isLDPOther ? 'required' : ''"
                        name="ldp_contact_person"
                        :class="{ 'is-invalid': errors.has('ldp_contact_person') }">
                      <span class="text-danger small" v-if="errors.has('ldp_contact_person')">
                        {{ errors.first('ldp_contact_person') }}
                      </span>
                    </div>

                    <!-- Email -->
                    <div class="col-md-6 mb-3">
                      <label class="form-label">
                        Email<span class="text-danger">*</span>
                      </label>
                      <input 
                        type="email" 
                        class="form-control" 
                        placeholder="email@example.com"
                        v-model="formData.ldpOtherEmail"
                        v-validate="isLDPOther ? 'required|email' : ''"
                        name="ldp_email"
                        :class="{ 'is-invalid': errors.has('ldp_email') }">
                      <span class="text-danger small" v-if="errors.has('ldp_email')">
                        {{ errors.first('ldp_email') }}
                      </span>
                    </div>

                    <!-- Phone -->
                    <div class="col-md-6 mb-3">
                      <label class="form-label">
                        Phone<span class="text-danger">*</span>
                      </label>
                      <input 
                        type="tel" 
                        class="form-control" 
                        placeholder="+62 812 3456 7890"
                        v-model="formData.ldpOtherPhone"
                        v-validate="isLDPOther ? 'required' : ''"
                        name="ldp_phone"
                        :class="{ 'is-invalid': errors.has('ldp_phone') }">
                      <span class="text-danger small" v-if="errors.has('ldp_phone')">
                        {{ errors.first('ldp_phone') }}
                      </span>
                    </div>

                    <!-- Provider Icon -->
                    <div class="col-md-6 mb-3">
                      <label class="form-label">
                        Provider Icon
                      </label>
                      <div 
                        class="upload-area text-center p-4 border rounded"
                        @dragover.prevent="onDragOver"
                        @dragleave.prevent="onDragLeave"
                        @drop.prevent="onDropProviderLogo"
                        :class="{ 'drag-active': isDraggingProviderLogo }">
                        
                        <!-- Show preview if image is uploaded -->
                        <div v-if="providerLogoPreview" class="position-relative">
                          <img 
                            :src="providerLogoPreview" 
                            alt="Provider Logo Preview" 
                            class="img-fluid rounded mb-2"
                            style="max-height: 150px; object-fit: contain;">
                          <div class="mt-2">
                            <button 
                              type="button"
                              class="btn btn-sm btn-outline-danger me-2" 
                              @click="removeProviderLogo">
                              <i class="fe fe-trash-2 me-1"></i>
                              Remove
                            </button>
                            <button 
                              type="button"
                              class="btn btn-sm btn-danger" 
                              @click="$refs.providerLogoInput.click()">
                              <i class="fe fe-refresh-cw me-1"></i>
                              Change
                            </button>
                          </div>
                        </div>

                        <!-- Show upload area if no image -->
                        <div v-else>
                          <i class="fe fe-upload-cloud mb-2" style="font-size: 2rem; color: #6c757d;"></i>
                          <p class="mb-2 font-weight-bold">Drag and Drop the Image</p>
                          <p class="text-muted small mb-2">Upload file .jpeg/.jpg/.png (Max. size 500Kb)</p>
                          <p class="text-muted small mb-3">(Image recommendation 512 x 512)</p>
                          <button 
                            type="button"
                            class="btn btn-sm btn-danger"
                            @click="$refs.providerLogoInput.click()">
                            Upload
                          </button>
                        </div>
                      </div>
                      
                      <!-- Hidden file input -->
                      <input 
                        ref="providerLogoInput"
                        type="file" 
                        accept="image/jpeg,image/jpg,image/png"
                        style="display: none"
                        @change="onProviderLogoChange">
                    </div>
                  </template>
                </div>
              </div>

              <!-- Step 4: Course Scope -->
              <div v-if="currentStep === 4">
                <h4 class="mb-4 font-weight-bold">Course Scope</h4>

                <div class="row">
                  <!-- Course Scope - Multiple Select -->
                  <div class="col-md-6 mb-3">
                    <label class="form-label">
                      Course Scope<span class="text-danger">*</span>
                    </label>
                    <b-form-checkbox-group
                      v-model="formData.courseScope"
                      :options="courseScopeOptions"
                      v-validate="'required'"
                      name="course_scope"
                      @change="onCourseScopeChange">
                    </b-form-checkbox-group>
                    <span class="text-danger small" v-if="errors.has('course_scope')">
                      Please select at least one scope
                    </span>
                  </div>

                  <!-- Division - Show only if BIB-HO or BIB-Site selected -->
                  <div v-if="shouldShowDeptDiv" class="col-md-6 mb-3">
                    <label class="form-label">
                      Division
                    </label>
                    <v-select
                      v-model="formData.division"
                      :options="divisionOptions"
                      label="label"
                      :reduce="option => option.value"
                      placeholder="Select Division"
                      :loading="isLoadingDivisions"
                      name="division"
                      :class="{ 'is-invalid': errors.has('division') }"
                      @input="onDivisionChange"
                      :clearable="true">
                      <template #option="option">
                        <div>{{ option.label }}</div>
                        <small v-if="option.code" class="text-muted">Code: {{ option.code }}</small>
                      </template>
                    </v-select>
                    <span class="text-danger small" v-if="errors.has('division')">
                      {{ errors.first('division') }}
                    </span>
                  </div>

                  <!-- Department - Show only if BIB-HO or BIB-Site selected, enable when division selected -->
                  <div v-if="shouldShowDeptDiv" class="col-md-6 mb-3">
                    <label class="form-label">
                      Department
                    </label>
                    <v-select
                      v-model="formData.department"
                      :options="departmentOptions"
                      label="label"
                      :reduce="option => option.value"
                      placeholder="Select Department"
                      :loading="isLoadingDepartments"
                      name="department"
                      :class="{ 'is-invalid': errors.has('department') }"
                      :disabled="!isDepartmentEnabled"
                      :clearable="true">
                      <template #option="option">
                        <div>{{ option.label }}</div>
                        <small v-if="option.code" class="text-muted">Code: {{ option.code }}</small>
                      </template>
                      <template #no-options>
                        <span v-if="!formData.division">Please select a division first</span>
                        <span v-else>No departments available</span>
                      </template>
                    </v-select>
                    <span class="text-danger small" v-if="errors.has('department')">
                      {{ errors.first('department') }}
                    </span>
                  </div>

                  <!-- Organization Level -->
                  <div class="col-md-6 mb-3">
                    <label class="form-label">
                      Organization Level
                    </label>
                    <v-select
                      v-model="formData.organizationLevel"
                      :options="organizationLevelOptions"
                      :reduce="option => option.value"
                      placeholder="Select Level"
                      :clearable="true">
                    </v-select>
                  </div>
                </div>
              </div>

              <!-- Step 5: Event/Batch - Hidden in edit mode -->
              <div v-if="currentStep === 5 && !editMode">
                <div class="d-flex justify-content-between align-items-center mb-4">
                  <h4 class="mb-0 font-weight-bold">Event/Batch</h4>
                  <button class="btn btn-danger btn-sm" @click="addBatch">
                    <i class="fe fe-plus me-1"></i>
                    Add Batch
                  </button>
                </div>
                <!-- Batch Table -->
                <div class="table-responsive">
                  <table class="table table-hover">
                    <thead>
                      <tr>
                        <th>BATCH</th>
                        <th>PENYELENGGARA</th>
                        <th>QUOTA</th>
                        <th>SCHEDULE</th>
                        <th>STATUS</th>
                        <th>ACTION</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="formData.batches.length === 0">
                        <td colspan="6" class="text-center text-muted py-4">
                          No batch added yet. Click "Add Batch" to create one.
                        </td>
                      </tr>
                      <tr v-for="(batch, index) in formData.batches" :key="index">
                        <td class="font-weight-bold">{{ batch.name }}</td>
                        <td>{{ batch.penyelenggara || '-' }}</td>
                        <td>{{ batch.quota }}</td>
                        <td>
                          <div v-for="(schedule, indexsc) in batch.schedules" :key="indexsc">
                            <div>{{ schedule.date }}</div>
                            <div class="text-muted small">{{ schedule.startTime }} - {{ schedule.endTime }}</div>
                          </div>
                        </td>
                        <td>
                          <span
                            :class="getStatusBadgeClass(batch.status)">
                            {{ formatStatus(batch.status) }}
                          </span>
                        </td>
                        <td>
                          <div class="d-flex gap-2">
                            <button 
                              class="btn btn-sm btn-icon btn-success" 
                              @click="duplicateBatch(index)"
                              title="Duplicate">
                              <i class="fe fe-copy"></i>
                            </button>
                            <button 
                              class="btn btn-sm btn-icon btn-warning" 
                              @click="editBatch(index)"
                              title="Edit">
                              <i class="fe fe-edit"></i>
                            </button>
                            <button 
                              class="btn btn-sm btn-icon btn-danger" 
                              @click="deleteBatch(index)"
                              title="Delete">
                              <i class="fe fe-trash-2"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            
          </div>
        </div>
      </div>
      <!-- Footer Buttons -->
      <div class="modal-footer d-flex justify-content-end gap-3 border-top">
        <button 
          v-if="shouldShowSaveDraft"
          class="btn btn-outline-danger px-4"
          @click="saveDraft">
          Save as draft
        </button>
        <button 
          v-if="editMode ? currentStep < 4 : currentStep < 5"
          class="btn btn-danger px-4"
          @click="nextStep">
          Next
        </button>
        <button 
          v-else
          class="btn btn-danger px-4"
          @click="showSubmitConfirmation">
          {{ editMode ? 'Update' : 'Submit' }}
        </button>
      </div>
    </div>

    <!-- Submit Confirmation Modal -->
    <b-modal
      id="modal-submit-confirmation"
      centered
      hide-header
      hide-footer
      size="sm">
      <div class="text-center p-3">
        <h5 class="mb-3 font-weight-bold">Submit course data?</h5>
        <p class="text-muted mb-4">You can't edit it after the schedule is created.</p>
        
        <div class="d-flex gap-3 justify-content-center">
          <button 
            class="btn btn-outline-danger px-4"
            @click="cancelSubmit">
            Cancel
          </button>
          <button 
            class="btn btn-danger px-4"
            @click="confirmSubmit">
            Confirm
          </button>
        </div>
      </div>
    </b-modal>

    <!-- Add Batch Modal -->
    <modal-add-batch 
      ref="modalAddBatch"
      :platform="formData.platform"
      :competency-type="formData.competency_type"
      :wait-for-api-response="false"
      @batch-added="onBatchAdded"
      @batch-updated="onBatchUpdated" />
  </b-modal>
</template>

<script>
import ModalAddBatch from './ModalAddBatch.vue';
import { mapGetters, mapActions } from 'vuex';
import { formatBatchLabel } from '@/utils/batchIdentity';

export default {
  name: 'ModalAddCourse',
  components: {
    ModalAddBatch
  },
  props: {
    initialTrainingId: {
      type: [Number, String],
      default: null
    }
  },
  computed: {
    ...mapGetters('courseCatalog', {
      isSubmitting: 'getLoading'
    }),
    ...mapGetters('modalAddCourse', [
      // Step 1
      'typeOptions',
      'roleOptions',
      'competencyOptions',
      'matrixKPLHOptions',
      'isLoadingRoles',
      'isLoadingCompetencies',
      // Step 2
      'courseTypeOptions',
      'categoryOptions',
      'stemMainOptions',
      'stemSubOptions',
      'yearOptions',
      'monthOptions',
      'isLoadingCourseTypes',
      'isLoadingCategories',
      // Step 3
      'platformOptions',
      'lspOptions',
      'trainingProviderOptions',
      'isLoadingLSP',
      'isLoadingTrainingProvider',
      // Step 4
      'departmentOptions',
      'divisionOptions',
      'organizationLevelOptions',
      'isLoadingDepartments',
      'isLoadingDivisions',
      'isLoadingOrganizationLevels'
    ]),
    ...mapGetters('users', ['profileData']),

    // Get current user roles
    userRoles() {
      return this.profileData?.roles_uenroll || [];
    },

    // Check if user is hsetraining
    isHSETrainingRole() {
      return this.userRoles.includes('hsetraining');
    },

    // Check if user is hrsiteoperation
    isHRSiteOperationRole() {
      return this.userRoles.includes('hrsiteoperation');
    },

    // Check if role field is required
    isRoleRequired() {
      return this.isHSETrainingRole;
    },

    // Check if matrixKPLH field is required
    isMatrixKPLHRequired() {
      return this.isHSETrainingRole;
    },

    // Competency rows are optional for add and edit.
    areCompetenciesRequired() {
      return false;
    },

    // Computed property for STEM sub options based on main selection
    currentStemSubOptions() {
      if (this.formData.stemCategoryMain) {
        return this.stemSubOptions[this.formData.stemCategoryMain] || [];
      }
      return [];
    },

    // Computed property for month options based on selected year
    currentMonthOptions() {
      const selectedYear = this.formData.certificateValidityYear || 0;
      const maxTotalMonths = 60; // 5 years = 60 months
      const usedMonths = selectedYear * 12;
      const availableMonths = maxTotalMonths - usedMonths;
      
      return this.monthOptions.filter(month => month.value <= availableMonths);
    },

    // Check if month select should be disabled
    isMonthDisabled() {
      return (this.formData.certificateValidityYear || 0) >= 5;
    },

    // Calculate total months selected
    totalValidityMonths() {
      return (this.formData.certificateValidityYear || 0) * 12 + (this.formData.certificateValidityMonth || 0);
    },

    // Check if LDP is "Other" to show additional fields
    isLDPOther() {
      return this.formData.trainingInstitution === 'other';
    },

    // Check if course scope includes BIB-HO or BIB-Site (show dept/div fields)
    shouldShowDeptDiv() {
      const courseScope = this.formData.courseScope || [];
      return courseScope.includes('BIB-HO') || courseScope.includes('BIB-Site');
    },

    // Check if department select should be enabled (need division selected)
    isDepartmentEnabled() {
      return this.shouldShowDeptDiv && this.formData.division;
    },

    // Determine if we should pass is_partner=true to organization levels API
    shouldUsePartnerParam() {
      const courseScope = this.formData.courseScope || [];
      // Only BIB-Partner selected (no BIB-HO or BIB-Site)
      return courseScope.includes('BIB-Partner') && 
             !courseScope.includes('BIB-HO') &&
             !courseScope.includes('BIB-Site');
    },

    // Check if should show "Save as Draft" button
    // Hide if course is already active (is_active = true)
    shouldShowSaveDraft() {
      // If not in edit mode, always show
      if (!this.editMode) return true;
      
      // If in edit mode, hide if course is active
      return !this.courseIsActive;
    }
  },
  data() {
    return {
      currentStep: 1, // Start from step 1
      editMode: false, // Flag to check if editing existing course
      trainingId: null, // Training ID for edit mode
      courseIsActive: false, // Track if course is already active      // Image preview states
      courseCoverPreview: null,
      providerLogoPreview: null,
      
      // Drag states
      isDraggingCourseCover: false,
      isDraggingProviderLogo: false,
      
      // Batch edit state
      editingBatchIndex: null, // Track which batch is being edited
      
      formData: {
        // Step 1: Competency/Matrix KPLH
        competency_type: '',
        role: '',
        matrixKPLH: '',
        competencies: [
          {
            competency: '',
            proficiency: null // Single selection (1-5)
          }
        ],
        
        // Step 2: Course Details
        courseName: '',
        noCourse: '',
        courseType: '',
        category: '',
        stemCategoryMain: '', // STEM or Non STEM
        stemCategorySub: '', // Sub category based on main selection
        learningHours: 0,
        courseUrl: '',
        certificateValidityYear: 0,
        certificateValidityMonth: 0,
        certificateValidityInMonths: 0, // Total months for API
        description: '',
        courseCover: null,
        removeCourseCover: false,
        isServiceBond: false,
        
        // Step 3: Platform Details
        platform: '',
        providerName: '', // LSP ID
        trainingInstitution: '', // LDP ID or 'other'
        // Fields for LDP "Other"
        ldpOtherName: '',
        ldpOtherAddress: '',
        ldpOtherWebsite: '',
        ldpOtherContactPerson: '',
        ldpOtherEmail: '',
        ldpOtherPhone: '',
        ldpOtherImage: null,
        
        // Step 4: Course Scope
        courseScope: [], // Multiple select: ['BIB-HO', 'BIB-Site', 'BIB-Partner']
        department: '',  // Will be array of IDs
        division: '',    // Will be array of IDs
        organizationLevel: '', // Will be array of IDs
        
        // Step 5: Event/Batch
        batches: []
      },
      courseScopeOptions: ['BIB-HO', 'BIB-Site', 'BIB-Partner'],
    }
  },
  mounted() {
    // Listen for modal shown event to check for edit mode
    this.$root.$on('bv::modal::shown', (bvEvent, modalId) => {
      if (modalId === 'modal-add-course') {
        this.loadStep1Data();
        this.loadStep2Data();
        this.loadStep3Data();
        this.loadStep4Data();
        // Check if there's a training ID passed via props or event
        if (this.initialTrainingId) {
          this.loadCourseDetail(this.initialTrainingId);
        }
      }
    });

    // Listen for edit course event
    this.$root.$on('open-edit-course', async (payload) => {
      if (payload && payload.mode === 'edit' && payload.data) {
        await this.populateFormForEdit(payload.data);
      }
    });
  },

  beforeDestroy() {
    this.$root.$off('bv::modal::shown');
    this.$root.$off('open-edit-course');
  },

  methods: {
    ...mapActions('modalAddCourse', [
      'fetchCompetencies',
      'fetchRoleCompetencies',
      'fetchCourseTypes',
      'fetchCategories',
      'fetchLSP',
      'fetchTrainingProvider',
      'fetchDepartments',
      'fetchDivisions',
      'fetchOrganizationLevels'
    ]),
    ...mapActions('courseCatalog', ['saveCourse', 'getCourseById']),

    // Format status: remove underscore and capitalize each word
    formatStatus(status) {
      if (!status) return '';
      return status
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    },

    getStatusBadgeClass(status) {
      if (!status) return 'badge badge-pill px-3 py-2';
      
      if (status === 'upcoming') {
        return 'badge badge-pill px-3 py-2 badge-warning';
      } else if (status === 'in_progress') {
        return 'badge badge-pill px-3 py-2 badge-info';
      } else if (status === 'compeleted' || status === 'completed') {
        return 'badge badge-pill px-3 py-2 badge-success';
      }
      
      // Default
      return 'badge badge-pill px-3 py-2 badge-secondary';
    },

    normalizeStemCategoryForPayload(mainCategory, subCategory) {
      if (!mainCategory || !subCategory) return '';

      if (mainCategory === 'Non STEM') {
        const nonStemValueMap = {
          'Human Resources': 'HR',
          'Health, Safety, Environment': 'HSE',
          'Supply Chain Management': 'SCM'
        };
        const normalizedSubCategory = nonStemValueMap[subCategory] || subCategory;

        return normalizedSubCategory.startsWith('Non-STEM ') ? normalizedSubCategory : `Non-STEM ${normalizedSubCategory}`;
      }

      const stemValueMap = {
        Math: 'Mathematics'
      };

      return stemValueMap[subCategory] || subCategory;
    },

    parseStemCategoryForForm(stemCategory) {
      if (!stemCategory) {
        return {
          main: '',
          sub: ''
        };
      }

      const normalizedValue = String(stemCategory).trim();
      const normalizedUpper = normalizedValue.toUpperCase();
      const nonStemPrefix = 'NON-STEM ';
      const nonStemDisplayMap = {
        HR: 'Human Resources',
        HSE: 'Health, Safety, Environment',
        SCM: 'Supply Chain Management'
      };

      if (normalizedUpper.startsWith(nonStemPrefix)) {
        const subValue = normalizedValue.slice(nonStemPrefix.length);

        return {
          main: 'Non STEM',
          sub: nonStemDisplayMap[subValue.toUpperCase()] || subValue
        };
      }

      if (normalizedUpper === 'NON STEM') {
        return {
          main: 'Non STEM',
          sub: 'General Management & Leadership'
        };
      }

      if (normalizedUpper === 'STEM') {
        return {
          main: 'STEM',
          sub: 'Science'
        };
      }

      const stemSub = normalizedUpper === 'MATHEMATICS' ? 'Math' : normalizedValue;
      const stemOptions = this.stemSubOptions.STEM || [];

      if (stemOptions.includes(stemSub)) {
        return {
          main: 'STEM',
          sub: stemSub
        };
      }

      return {
        main: 'Non STEM',
        sub: nonStemDisplayMap[normalizedUpper] || normalizedValue
      };
    },

    // Populate form with course data for edit mode
    async populateFormForEdit(courseData) {
      
      this.editMode = true;
      this.trainingId = courseData.id;
      this.courseIsActive = courseData.is_active || false;

      // Step 1: Competency & Basic Info
      this.formData.courseName = courseData.title || '';
      this.formData.type = courseData.training_type || '';

      this.formData.competency_type = courseData.role_competency_type || '';
      this.formData.role = courseData.role_competency_id || '';
      this.formData.matrixKPLH = courseData.kplh_status || '';

      
      // Map competencies - extract type, role, matrixKPLH from first competency
      if (courseData.competency && courseData.competency.length > 0) {
        // Map only competency and proficiency
        this.formData.competencies = courseData.competency.map(comp => ({
          competency: comp.id || '',
          proficiency: comp.proficiency && comp.proficiency.length > 0 ? comp.proficiency[0] : 1
        }));
      } else {
        this.formData.competencies = [{
          competency: '',
          proficiency: null
        }];
      }

      // Step 2: Course Details
      this.formData.courseType = courseData.training_type || '';
      this.formData.category = courseData.training_category_id || '';
      this.formData.learningHours = courseData.hour ? Number(courseData.hour) : '';
      this.formData.courseUrl = courseData.website || '';
      this.formData.description = courseData.description || '';
      this.formData.isServiceBond = courseData.is_service_bond === true || courseData.is_service_bond === 1 || courseData.is_service_bond === '1';
      this.formData.noCourse = courseData.no_course || '';
      
      // Certificate validity
      if (courseData.certificate_validity_period) {
        const totalMonths = courseData.certificate_validity_period;
        this.formData.certificateValidityYear = Math.floor(totalMonths / 12);
        this.formData.certificateValidityMonth = totalMonths % 12;
        this.formData.certificateValidityInMonths = totalMonths;
      }

      // STEM & Non STEM Category
      const parsedStemCategory = this.parseStemCategoryForForm(courseData.stem_category);
      this.formData.stemCategoryMain = parsedStemCategory.main;
      this.formData.stemCategorySub = parsedStemCategory.sub;

      // Images (only set preview paths, don't set actual files)
      if (courseData.cover_image_path) {
        this.courseCoverPreview = `${process.env.VUE_APP_API_URL_UGEMS_UNIV}/attachments/${courseData.cover_image_path}`;
      }
      this.formData.removeCourseCover = false;
      if (courseData.provider_logo_path) {
        this.providerLogoPreview = `${process.env.VUE_APP_API_URL_UGEMS_UNIV}/attachments/${courseData.provider_logo_path}`;
      }

      // Step 3: Training Provider
      this.formData.platform = courseData.platform || '';
      this.formData.trainingInstitution = courseData.provider_id || '';
      this.formData.providerName = courseData.lsp_id || '';
      
      // Provider details (if "Other" selected)
      // if (courseData.provider_name) {
      if (!courseData.provider_id) {
        this.formData.providerName = courseData.provider_name;
        this.formData.providerWebsite = courseData.provider_website || '';
        this.formData.providerAddress = courseData.provider_address || '';
        this.formData.ldpOtherContactPerson = courseData.provider_contact_person || '';
        this.formData.ldpOtherPhone = courseData.provider_phone || '';
        this.formData.ldpOtherEmail = courseData.provider_email || '';
      }

      // Step 4 Course Scope
      this.formData.courseScope = courseData.training_scope || [];
      // this.$set(this.formData, 'courseScope', ['BIB-HO', 'BIB-Site']);
      // if (courseData.training_scope && Array.isArray(courseData.training_scope)) {
      //   this.formData.courseScope = JSON.parse(JSON.stringify(courseData.training_scope));
        
      //   console.log('✅ courseScope (no observer):', this.formData.courseScope);
      // }
      
      this.formData.division = courseData.division || '';
      this.formData.organizationLevel = courseData.position_level_id || '';
      
      // Fetch departments based on division if division exists
      if (this.formData.division) {
        try {
          await this.fetchDepartments(this.formData.division);
          // Set department after departments are loaded
          this.formData.department = courseData.department || '';
        } catch (error) {
          console.error('Error loading departments for edit:', error);
          this.formData.department = courseData.department || '';
        }
      } else {
        this.formData.department = courseData.department || '';
      }

      // Step 5: Batches
      if (courseData.batches && courseData.batches.length > 0) {
        this.formData.batches = courseData.batches.map((batch) => ({
          id: batch.id,
          batch: batch.batch,
          name: formatBatchLabel(batch),
          courseMethod: batch.training_method_id || '',
          coursePrice: batch.course_price || '',
          coursePriceCurrency: 'IDR',
          accommodationPrice: batch.accomodation_price || '',
          accommodationCurrency: 'IDR',
          transportPrice: batch.transportation_price || '',
          transportCurrency: 'IDR',
          location: batch.location || '',
          quota: batch.quota || '',
          passing_score: batch.passing_score || null,
          penyelenggara: batch.penyelenggara || 'PT BORNEO INDOBARA',
          certificate_id: batch.certificate_id || null,
          status: batch.status || null,
          schedules: batch.schedules && batch.schedules.length > 0 
            ? batch.schedules.map(schedule => ({
                id: schedule.id, // Keep ID for existing schedules
                date: schedule.date || '',
                startTime: schedule.start_time ? schedule.start_time.substring(0, 5) : '', // Format: HH:mm
                endTime: schedule.end_time ? schedule.end_time.substring(0, 5) : '',
                status: schedule.status
              }))
            : [{ date: '', startTime: '', endTime: '' }],
          participants: batch.participants && batch.participants.length > 0
            ? batch.participants.map(p => ({
                employee: {
                  value: p.nik,
                  label: `${p.name} - ${p.nik}`,
                  full_name: p.name,
                  nik: p.nik,
                  position: p.position || '-'
                },
                position: p.position || '-'
              }))
            : []
        }));
      }

      // Set current step to 1
      this.currentStep = 1;
    },

    // Load data for Step 1
    async loadStep1Data() {
      try {
        await Promise.all([
          this.fetchCompetencies(),
          this.fetchRoleCompetencies()
        ]);
      } catch (error) {
        this.$swal('Error', 'Data langkah 1 belum berhasil dimuat', 'error');
      }
    },

    // Load data for Step 2
    async loadStep2Data() {
      try {
        await Promise.all([
          this.fetchCourseTypes(),
          this.fetchCategories()
        ]);
      } catch (error) {
        this.$swal('Error', 'Data langkah 2 belum berhasil dimuat', 'error');
      }
    },

    // Load data for Step 3
    async loadStep3Data() {
      try {
        await Promise.all([
          this.fetchLSP(),
          this.fetchTrainingProvider()
        ]);
      } catch (error) {
        this.$swal('Error', 'Data langkah 3 belum berhasil dimuat', 'error');
      }
    },

    // Load data for Step 4
    async loadStep4Data() {
      try {
        await this.fetchDivisions();
        // Load organization levels based on initial course scope
        await this.loadOrganizationLevels();
      } catch (error) {
        this.$swal('Error', 'Data langkah 4 belum berhasil dimuat', 'error');
      }
    },

    // Load organization levels with conditional is_partner param
    async loadOrganizationLevels() {
      try {
        await this.fetchOrganizationLevels(this.shouldUsePartnerParam);
      } catch (error) {
        console.error('Error loading organization levels:', error);
      }
    },

    // Watch for STEM Category Main change to reset sub category
    onStemMainChange() {
      this.formData.stemCategorySub = ''; // Reset sub category when main changes
    },

    // Watch for Course Scope change
    async onCourseScopeChange() {
      // Reset division and department if no longer showing those fields
      if (!this.shouldShowDeptDiv) {
        this.formData.division = '';
        this.formData.department = '';
      }
      
      // // Reload organization levels with correct params
      // await this.loadOrganizationLevels();
    },

    // Watch for Division change to load departments
    async onDivisionChange() {
      this.formData.department = ''; // Reset department when division changes
      
      if (this.formData.division) {
        try {
          await this.fetchDepartments(this.formData.division);
        } catch (error) {
          this.$swal('Error', 'Departemen belum berhasil dimuat', 'error');
        }
      }
    },

    // Watch for LDP change to reset "Other" fields
    onLDPChange() {
      if (!this.isLDPOther) {
        // Reset all "Other" fields when selecting non-Other option
        this.formData.ldpOtherName = '';
        this.formData.ldpOtherAddress = '';
        this.formData.ldpOtherWebsite = '';
        this.formData.ldpOtherContactPerson = '';
        this.formData.ldpOtherEmail = '';
        this.formData.ldpOtherPhone = '';
        this.formData.ldpOtherImage = null;
      }
    },

    // Certificate Validity Period Methods
    onValidityYearChange() {
      // If year is 5, set month to 0 and disable month selection
      if (this.formData.certificateValidityYear >= 5) {
        this.formData.certificateValidityMonth = 0;
      }
      
      // Check if current month selection exceeds limit
      const maxAllowedMonths = (5 - this.formData.certificateValidityYear) * 12;
      if (this.formData.certificateValidityMonth > maxAllowedMonths) {
        this.formData.certificateValidityMonth = 0;
      }
      
      this.updateValidityPeriodInMonths();
    },

    onValidityMonthChange() {
      this.updateValidityPeriodInMonths();
    },

    updateValidityPeriodInMonths() {
      // Calculate total months for API
      this.formData.certificateValidityInMonths = this.totalValidityMonths;
    },

    // Step 1: Competency Methods
    addCompetency() {
      this.formData.competencies.push({
        competency: '',
        proficiency: null // Single selection
      });
    },

    removeCompetency(index) {
      if (this.formData.competencies.length > 1) {
        this.formData.competencies.splice(index, 1);
      }
    },

    selectProficiency(competencyIndex, level) {
      // Single selection, click again to clear when competency is optional.
      const currentValue = this.formData.competencies[competencyIndex].proficiency;
      this.formData.competencies[competencyIndex].proficiency = currentValue === level ? null : level;
    },

    onCompetencyChange(competencyIndex) {
      if (!this.formData.competencies[competencyIndex].competency) {
        this.formData.competencies[competencyIndex].proficiency = null;
      }
    },

    // Image Upload Methods
    onCourseCoverChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.validateAndSetCourseCover(file);
      }
    },

    onProviderLogoChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.validateAndSetProviderLogo(file);
      }
    },

    validateAndSetCourseCover(file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        this.$swal('Error', 'Unggah file gambar yang valid (.jpeg/.jpg/.png)', 'error');
        return;
      }

      // Validate file size (800KB = 819200 bytes)
      const maxSize = 800 * 1024; // 800KB in bytes
      if (file.size > maxSize) {
        this.$swal('Error', 'Ukuran gambar harus kurang dari 800KB', 'error');
        return;
      }

      // Set file and create preview
      this.formData.courseCover = file;
      this.formData.removeCourseCover = false;
      this.createImagePreview(file, 'courseCoverPreview');
    },

    validateAndSetProviderLogo(file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        this.$swal('Error', 'Unggah file gambar yang valid (.jpeg/.jpg/.png)', 'error');
        return;
      }

      // Validate file size (500KB = 512000 bytes)
      const maxSize = 500 * 1024; // 500KB in bytes
      if (file.size > maxSize) {
        this.$swal('Error', 'Ukuran gambar harus kurang dari 500KB', 'error');
        return;
      }

      // Set file and create preview
      this.formData.ldpOtherImage = file;
      this.createImagePreview(file, 'providerLogoPreview');
    },

    createImagePreview(file, previewProperty) {
      const reader = new FileReader();
      reader.onload = (e) => {
        this[previewProperty] = e.target.result;
      };
      reader.readAsDataURL(file);
    },

    removeCourseCover() {
      this.formData.courseCover = null;
      this.formData.removeCourseCover = true;
      this.courseCoverPreview = null;
      if (this.$refs.courseCoverInput) {
        this.$refs.courseCoverInput.value = '';
      }
    },

    removeProviderLogo() {
      this.formData.ldpOtherImage = null;
      this.providerLogoPreview = null;
      if (this.$refs.providerLogoInput) {
        this.$refs.providerLogoInput.value = '';
      }
    },

    // Drag and Drop Methods
    onDragOver(event) {
      event.preventDefault();
      if (event.currentTarget.contains(event.target)) {
        if (event.currentTarget.querySelector('input[type="file"]') === this.$refs.courseCoverInput) {
          this.isDraggingCourseCover = true;
        } else if (event.currentTarget.querySelector('input[type="file"]') === this.$refs.providerLogoInput) {
          this.isDraggingProviderLogo = true;
        }
      }
    },

    onDragLeave(event) {
      event.preventDefault();
      if (!event.currentTarget.contains(event.relatedTarget)) {
        this.isDraggingCourseCover = false;
        this.isDraggingProviderLogo = false;
      }
    },

    onDropCourseCover(event) {
      event.preventDefault();
      this.isDraggingCourseCover = false;
      
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.validateAndSetCourseCover(files[0]);
      }
    },

    onDropProviderLogo(event) {
      event.preventDefault();
      this.isDraggingProviderLogo = false;
      
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.validateAndSetProviderLogo(files[0]);
      }
    },

    // Step 5: Batch Methods
    addBatch() {
      this.editingBatchIndex = null; // Reset editing index
      this.$bvModal.show('modal-add-batch');
    },

    onBatchAdded(batchData) {
      console.log('batchData', batchData)
      console.log('index', this.formData.batches.length);
      
      const batchNumber = this.formData.batches.length + 1;
      // Transform batch data from modal to table format
      // const scheduleDate = batchData.schedules[0]?.date || '';
      // const scheduleTime = `${batchData.schedules[0]?.startTime || ''} - ${batchData.schedules[0]?.endTime || ''}`;
      
      this.formData.batches.push({
        name: `Batch ${batchNumber}`,
        quota: batchData.quota,
        // scheduleDate: this.formatDate(scheduleDate),
        // scheduleTime: scheduleTime,
        status: batchData.status,
        schedules: batchData.schedules,
        rawData: batchData // Store original data
      });
    },

    onBatchUpdated(batchData) {
      if (this.editingBatchIndex !== null) {
        // Update existing batch - replace entire object using splice for reactivity
        const currentBatch = this.formData.batches[this.editingBatchIndex];
        const updatedBatch = {
          ...currentBatch,
          quota: batchData.quota,
          status: batchData.status,
          schedules: batchData.schedules,
          rawData: batchData
        };
        
        this.formData.batches.splice(this.editingBatchIndex, 1, updatedBatch);
        this.editingBatchIndex = null;
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const options = { day: 'numeric', month: 'long', year: 'numeric' };
      return date.toLocaleDateString('en-GB', options);
    },

    duplicateBatch(index) {
      const originalBatch = this.formData.batches[index];
      const batchNumber = this.formData.batches.length + 1;
      
      const rawDataCopy = JSON.parse(JSON.stringify(originalBatch.rawData || originalBatch));
      delete rawDataCopy.id; // Remove id from duplicated batch
      
      if (rawDataCopy.schedules && Array.isArray(rawDataCopy.schedules)) {
        rawDataCopy.schedules = rawDataCopy.schedules.map(schedule => {
          const { id, ...scheduleWithoutId } = schedule;
          return scheduleWithoutId;
        });
      }
      
      const duplicatedBatch = {
        name: `Batch ${batchNumber}`,
        quota: originalBatch.quota,
        status: originalBatch.status,
        schedules: rawDataCopy.schedules,
        rawData: rawDataCopy
      };
      
      this.formData.batches.push(duplicatedBatch);
      
      this.$swal({
        title: 'Diduplikasi',
        text: 'Batch berhasil diduplikasi.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      });
    },

    editBatch(index) {
      this.editingBatchIndex = index;
      const batch = this.formData.batches[index];
      
      // Pass batch data to modal for editing
      if (this.$refs.modalAddBatch && this.$refs.modalAddBatch.loadBatchData) {
        this.$refs.modalAddBatch.loadBatchData(batch.rawData || batch);
      }
      
      this.$bvModal.show('modal-add-batch');
    },

    deleteBatch(index) {
      this.$swal({
        title: 'Hapus Batch?',
        text: 'Apakah Anda yakin ingin menghapus batch ini?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#e63757',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Ya, hapus'
      }).then((result) => {
        if (result.isConfirmed) {
          this.formData.batches.splice(index, 1);
          this.$swal('Terhapus', 'Batch berhasil dihapus.', 'success');
        }
      });
    },

    // Navigation Methods
    closeModal() {
      // Reset form
      this.resetForm();
      this.$bvModal.hide('modal-add-course');
    },

    resetForm() {
      this.editMode = false;
      this.trainingId = null;
      this.courseIsActive = false;
      this.currentStep = 1;
      this.courseCoverPreview = null;
      this.providerLogoPreview = null;
      this.editingBatchIndex = null;
      
      // Reset validator errors
      this.$validator.reset();
      
      // Reset form data to initial state
      this.formData = {
        competency_type: '',
        role: '',
        matrixKPLH: '',
        competencies: [{
          competency: '',
          proficiency: null
        }],
        courseName: '',
        type: '',
        courseType: '',
        category: '',
        learningHour: '',
        courseURL: '',
        description: '',
        certificateValidityYear: 0,
        certificateValidityMonth: 0,
        certificateValidityInMonths: 0,
        stemCategoryMain: '',
        stemCategorySub: '',
        courseCover: null,
        removeCourseCover: false,
        platform: '',
        trainingInstitution: '',
        lsp: '',
        providerName: '',
        providerWebsite: '',
        providerAddress: '',
        providerContactPerson: '',
        providerPhone: '',
        providerEmail: '',
        providerLogo: null,
        courseScope: [],
        department: '',
        division: '',
        organizationLevel: '',
        batches: []
      };
    },

    // Handler when modal is hidden (closed)
    onModalHidden() {
      this.resetForm();
    },

    toStep(step) {
      // Prevent navigation to Step 5 when in edit mode
      if (this.editMode && step > 4) {
        return;
      }
      this.currentStep = step;
    },

    nextStep() {
      const maxStep = this.editMode ? 4 : 5;
      if (this.currentStep < maxStep) {
        this.currentStep++;
      }
    },

    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },

    // Map form data to API format
    mapFormDataToAPI(isDraft = false) {
      const formData = new FormData();

      // Root level fields
      if (this.editMode) {
        formData.append('training_id', this.trainingId);
      }

      formData.append('is_draft', isDraft);

      // Step 1: Competency (restructured format)
      // competency_type is always required; competency rows are optional.
      if (this.formData.competency_type) {
        const competencies = this.formData.competencies
          .filter(comp => comp.competency && comp.proficiency) // Only include filled competencies
          .map(comp => ({
            competency_id: comp.competency,
            proficiency: comp.proficiency // Single value (1-5)
          }));
        
        const competencyData = {
          competency_type: this.formData.competency_type
        };
        
        // Add role if filled
        if (this.formData.role) {
          competencyData.role_competency_id = this.formData.role;
        }
        
        // Add matrixKPLH if filled
        if (this.formData.matrixKPLH) {
          competencyData.kplh_matrix = this.formData.matrixKPLH;
        }
        
        // Add competencies array if there are any
        if (competencies.length > 0) {
          competencyData.competencies = competencies;
        }
        
        formData.append('competency', JSON.stringify(competencyData));
      }

      // Step 2: Course Detail
      // Only include if course name is filled (minimum requirement)
      if (this.formData.courseName) {
        const courseDetail = {
          course_name: this.formData.courseName,
          course_type: this.formData.courseType,
          no_course: this.formData.noCourse || undefined,
          training_category_id: this.formData.category || null,
          stem_category: this.normalizeStemCategoryForPayload(this.formData.stemCategoryMain, this.formData.stemCategorySub),
          learning_hours: parseInt(this.formData.learningHours) || 0,
          course_url: this.formData.courseUrl,
          certificate_validity_period: this.totalValidityMonths,
          description: this.formData.description,
          is_service_bond: this.formData.isServiceBond === true ? 1 : 0,
          remove_cover_image: this.formData.removeCourseCover === true
        };
        formData.append('course_detail', JSON.stringify(courseDetail));

        // Cover Image (Step 2)
        if (this.formData.courseCover) {
          formData.append('cover_image', this.formData.courseCover);
        }
      }

      // Step 3: Platform Detail
      // Only include if platform or provider is filled
      if (this.formData.platform || this.formData.providerName || this.formData.trainingInstitution) {
        const platformDetail = {
          platform: this.formData.platform,
          lsp_id: this.formData.providerName,
          provider: this.isLDPOther ? {
            name: this.formData.ldpOtherName,
            address: this.formData.ldpOtherAddress,
            website: this.formData.ldpOtherWebsite,
            contact_person: this.formData.ldpOtherContactPerson,
            email: this.formData.ldpOtherEmail,
            phone: this.formData.ldpOtherPhone
          } : this.formData.trainingInstitution
        };
        formData.append('platform_detail', JSON.stringify(platformDetail));

        // Provider Logo (Step 3 - if "Other")
        if (this.isLDPOther && this.formData.ldpOtherImage) {
          formData.append('provider_logo', this.formData.ldpOtherImage);
        }
      }

      // Step 4: Training Scope
      // Only include if at least one scope field is filled
      if (this.formData.courseScope.length > 0 || this.formData.department || 
          this.formData.division || this.formData.organizationLevel) {
        const trainingScope = {
          scope: this.formData.courseScope,
          department: this.formData.department,
          division: this.formData.division,
          position_level_id: this.formData.organizationLevel
        };
        formData.append('training_scope', JSON.stringify(trainingScope));
      }

      console.log('formData courseScope:', this.formData.batches);

      // Step 5: Batch
      // Only include if there are batches added and not in edit mode
      if (!this.editMode && this.formData.batches.length > 0) {
        const batchData = this.formData.batches.map(batch => {
          const rawBatch = batch.rawData || batch;
          return {
            id: rawBatch.id,
            method: rawBatch.courseMethod,
            course_price_currency: rawBatch.coursePriceCurrency,
            course_price: parseInt(rawBatch.coursePrice) || 0,
            accomodation_price_currency: rawBatch.accommodationCurrency,
            accomodation_price: parseInt(rawBatch.accommodationPrice) || 0,
            transportation_price_currency: rawBatch.transportCurrency,
            transportation_price: parseInt(rawBatch.transportPrice) || 0,
            passing_score: rawBatch.passing_score || null,
            penyelenggara: rawBatch.penyelenggara || 'PT BORNEO INDOBARA',
            location: rawBatch.location,
            quota: parseInt(rawBatch.quota) || 0,
            certificate_id: rawBatch.certificate_id,
            schedules: (rawBatch.schedules || []).map(schedule => ({
              date: schedule.date,
              start_time: schedule.startTime,
              end_time: schedule.endTime
            })),
            participants: (rawBatch.participants || []).map(p => p.employee?.nik || p)
          };
        });
        formData.append('batch', JSON.stringify(batchData));
      }

      return formData;
    },

    // Validate Step 2 (Course Detail) only - for Save as Draft
    async validateStep2() {
      // Manual validation for Step 2 fields
      const step2Errors = [];
      
      if (!this.formData.courseName || this.formData.courseName.trim() === '') {
        step2Errors.push('Course Name');
      }
      if (!this.formData.courseType) {
        step2Errors.push('Course Type');
      }
      if (!this.formData.stemCategoryMain) {
        step2Errors.push('STEM Category Main');
      }
      if (!this.formData.stemCategorySub) {
        step2Errors.push('STEM Category Sub');
      }
      if (!this.formData.learningHours || this.formData.learningHours < 1) {
        step2Errors.push('Learning Hours');
      }
      if (!this.formData.description || this.formData.description.trim() === '') {
        step2Errors.push('Description');
      }
      
      if (step2Errors.length > 0) {
        // Navigate to Step 2 if there are errors
        this.currentStep = 2;
        this.$swal({
          title: 'Validasi Gagal',
          text: `Lengkapi: ${step2Errors.join(', ')}`,
          icon: 'warning',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
        return false;
      }
      
      return true;
    },

    // Validate all steps - for Submit
    async validateAllSteps() {
      // Step 1 Validation: Competency
      const step1Errors = [];
      
      // Validate competency_type (always required)
      if (!this.formData.competency_type) {
        step1Errors.push('Type');
      }
      
      // Validate role (required for hsetraining)
      if (this.isRoleRequired && !this.formData.role) {
        step1Errors.push('Role');
      }
      
      // Validate matrixKPLH (required for hsetraining)
      if (this.isMatrixKPLHRequired && !this.formData.matrixKPLH) {
        step1Errors.push('Matrix KPLH');
      }
      
      // Competency rows are optional, but filled rows must be complete.
      this.formData.competencies.forEach((comp, index) => {
        if (comp.competency && !comp.proficiency) {
          step1Errors.push(`Competency ${index + 1}: Proficiency level`);
        }
        if (!comp.competency && comp.proficiency) {
          step1Errors.push(`Competency ${index + 1}: Competency field`);
        }
      });
      
      if (step1Errors.length > 0) {
        this.currentStep = 1;
        this.$swal({
          title: 'Validasi Gagal',
          text: `Lengkapi: ${step1Errors.join(', ')}`,
          icon: 'warning',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
        return false;
      }
      
      // Step 2 Validation: Course Details
      const step2Errors = [];
      if (!this.formData.courseName || this.formData.courseName.trim() === '') {
        step2Errors.push('Course Name');
      }
      if (!this.formData.courseType) {
        step2Errors.push('Course Type');
      }
      if (!this.formData.stemCategoryMain) {
        step2Errors.push('STEM Category Main');
      }
      if (!this.formData.stemCategorySub) {
        step2Errors.push('STEM Category Sub');
      }
      if (!this.formData.learningHours || this.formData.learningHours < 1) {
        step2Errors.push('Learning Hours');
      }
      if (!this.formData.description || this.formData.description.trim() === '') {
        step2Errors.push('Description');
      }
      
      if (step2Errors.length > 0) {
        this.currentStep = 2;
        this.$swal({
          title: 'Validasi Gagal',
          text: `Lengkapi: ${step2Errors.join(', ')}`,
          icon: 'warning',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
        return false;
      }
      
      // Step 3 Validation: Platform Details
      const step3Errors = [];
      if (!this.formData.platform) {
        step3Errors.push('Platform');
      }
      if (!this.formData.providerName) {
        step3Errors.push('Provider Name (LSP)');
      }
      if (!this.formData.trainingInstitution) {
        step3Errors.push('Training Institution (LDP)');
      }
      // Additional validation for LDP "Other"
      if (this.isLDPOther) {
        if (!this.formData.ldpOtherName || this.formData.ldpOtherName.trim() === '') {
          step3Errors.push('LDP Name');
        }
        if (!this.formData.ldpOtherAddress || this.formData.ldpOtherAddress.trim() === '') {
          step3Errors.push('LDP Address');
        }
        if (!this.formData.ldpOtherContactPerson || this.formData.ldpOtherContactPerson.trim() === '') {
          step3Errors.push('LDP Contact Person');
        }
        if (!this.formData.ldpOtherEmail || this.formData.ldpOtherEmail.trim() === '') {
          step3Errors.push('LDP Email');
        }
        if (!this.formData.ldpOtherPhone || this.formData.ldpOtherPhone.trim() === '') {
          step3Errors.push('LDP Phone');
        }
      }
      
      if (step3Errors.length > 0) {
        this.currentStep = 3;
        this.$swal({
          title: 'Validasi Gagal',
          text: `Lengkapi: ${step3Errors.join(', ')}`,
          icon: 'warning',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
        return false;
      }
      
      // Step 4 Validation: Course Scope
      const step4Errors = [];
      if (!this.formData.courseScope || this.formData.courseScope.length === 0) {
        step4Errors.push('Course Scope');
      }
      
      if (step4Errors.length > 0) {
        this.currentStep = 4;
        this.$swal({
          title: 'Validasi Gagal',
          text: `Lengkapi: ${step4Errors.join(', ')}`,
          icon: 'warning',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
        return false;
      }
      
      return true;
    },

    async saveDraft() {
      // Validate only Step 2 (Course Detail) for Save as Draft
      const isValid = await this.validateStep2();
      if (!isValid) return;
      
      try {
        const formData = this.mapFormDataToAPI(true);
        
        await this.saveCourse(formData);
        
        this.$swal("Tersimpan", "Pelatihan berhasil disimpan sebagai draf", "success");
        this.$emit('course-saved'); // Emit event untuk refresh list
        this.closeModal();
      } catch (error) {
        console.error('Error saving draft:', error);
        const errorMessage = error.response?.data?.error || error.response?.data?.message || "Pelatihan belum berhasil disimpan sebagai draf";
        this.$swal("Error", errorMessage, "error");
      }
    },

    async showSubmitConfirmation() {
      // Validate all steps before showing submit confirmation
      const isValid = await this.validateAllSteps();
      if (!isValid) return;
      
      this.$bvModal.show('modal-submit-confirmation');
    },

    cancelSubmit() {
      this.$bvModal.hide('modal-submit-confirmation');
    },

    async confirmSubmit() {
      try {
        const formData = this.mapFormDataToAPI(false);
        console.log('Submitting formData:', formData);
        await this.saveCourse(formData);
        
        this.$bvModal.hide('modal-submit-confirmation');
        this.$swal("Success", "Pelatihan berhasil dibuat", "success");
        this.$emit('course-created', this.formData);
        this.closeModal();
      } catch (error) {
        console.error('Error submitting course:', error);
        this.$bvModal.hide('modal-submit-confirmation');
        const errorMessage = error.response?.data?.error || error.response?.data?.message || "Pelatihan belum berhasil dibuat";
        this.$swal("Error", errorMessage, "error");
      }
    }
  }
}
</script>

<style scoped>
.modal-container {
  min-height: 600px;
}

.modal-header-custom {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.btn-close {
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.btn-close:hover {
  opacity: 1;
}

.modal-body-container {
  min-height: 500px;
}

/* Stepper Styles */
.stepper-sidebar {
  background-color: #f8f9fa;
}

.step-item {
  display: flex;
  gap: 1rem;
  position: relative;
  margin-bottom: 2rem;
}

.step-item:last-child {
  margin-bottom: 0;
}

.step-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}

.step-number {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #e9ecef;
  border: 2px solid #dee2e6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  color: #6c757d;
  transition: all 0.3s;
  z-index: 1;
}

.step-item.active .step-number {
  background-color: #fff;
  border-color: #e63757;
  color: #e63757;
}

.step-item.completed .step-number {
  background-color: #e63757;
  border-color: #e63757;
  color: white;
}

.step-line {
  width: 2px;
  flex: 1;
  background-color: #dee2e6;
  margin-top: 0.5rem;
}

.step-item:last-child .step-line {
  display: none;
}

.step-item.completed .step-line {
  background-color: #e63757;
}

.step-content {
  flex: 1;
  padding-top: 0.25rem;
}

.step-label {
  font-size: 0.75rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

.step-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.25rem;
}

.step-item.active .step-title {
  color: #e63757;
}

.step-status {
  font-size: 0.75rem;
  color: #6c757d;
  margin-top: 0.25rem;
}

.step-item.completed .step-status {
  color: #e63757;
}

.step-item.active .step-status {
  color: #e63757;
}

/* Status Badge Styles */
.badge-warning {
  background-color: #fef5e4 !important;
  color: #f6c343 !important;
  font-weight: 500;
}

.badge-info {
  background-color: #e4f4f7 !important;
  color: #39afd1 !important;
  font-weight: 500;
}

.badge-success {
  background-color: #d4edda !important;
  color: #00d97e !important;
  font-weight: 500;
}

.badge-secondary {
  background-color: #e9ecef !important;
  color: #6c757d !important;
  font-weight: 500;
}

/* Upload Area */
.upload-area {
  background-color: #f8f9fa;
  border: 2px dashed #dee2e6 !important;
  transition: all 0.3s;
  cursor: pointer;
}

.upload-area:hover {
  border-color: #e63757 !important;
  background-color: #fff5f7;
}

.upload-area.drag-active {
  border-color: #e63757 !important;
  background-color: #fff5f7;
  transform: scale(1.02);
}

/* Form Styles */
.form-control:focus,
.form-select:focus {
  border-color: #e63757;
  box-shadow: 0 0 0 0.2rem rgba(230, 55, 87, 0.25);
}

textarea.form-control {
  resize: none;
}

/* Proficiency Buttons */
.btn-proficiency {
  width: 40px;
  height: 40px;
  border: 2px solid #dee2e6;
  background-color: #fff;
  color: #6c757d;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s;
}

.btn-proficiency:hover {
  border-color: #e63757;
  color: #e63757;
}

.btn-proficiency.active {
  background-color: #e63757;
  border-color: #e63757;
  color: white;
}

.btn-proficiency:focus {
  box-shadow: 0 0 0 0.2rem rgba(230, 55, 87, 0.25);
}

/* V-Select Custom Styles */
/* ::v-deep .v-select .vs__dropdown-toggle {
  border-color: #ced4da;
  border-radius: 0.375rem;
  padding: 0.375rem 0.75rem;
}

::v-deep .v-select.vs--open .vs__dropdown-toggle {
  border-color: #e63757;
  box-shadow: 0 0 0 0.2rem rgba(230, 55, 87, 0.25);
}

::v-deep .v-select .vs__selected {
  color: #2d3748;
  margin: 0;
  padding: 0;
}

::v-deep .v-select .vs__search {
  margin: 0;
  padding: 0;
}

::v-deep .v-select .vs__search::placeholder {
  color: #6c757d;
}

::v-deep .v-select .vs__actions {
  padding: 0;
}

::v-deep .v-select .vs__dropdown-menu {
  border-color: #e63757;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

::v-deep .v-select .vs__dropdown-option--highlight {
  background-color: #e63757;
  color: white;
}

::v-deep .v-select .vs__dropdown-option {
  padding: 0.5rem 0.75rem;
}

::v-deep .v-select .vs__clear {
  margin-right: 0;
} */

/* Table Styles */
.table {
  margin-bottom: 0;
}

.table thead th {
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  color: #6c757d;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  padding: 1rem 0.75rem;
}

.table tbody td {
  padding: 1rem 0.75rem;
  vertical-align: middle;
}

.table-hover tbody tr:hover {
  background-color: #f8f9fa;
}

/* Badge Styles */
.badge {
  padding: 0.375rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 0.375rem;
}

.badge-warning {
  background-color: #ffc107;
  color: #000;
}

.badge-info {
  background-color: #17a2b8;
  color: #fff;
}

.badge-success {
  background-color: #28a745;
  color: #fff;
}

/* Button Icon */
.btn-icon {
  width: 32px;
  height: 32px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.375rem;
}

.btn-success {
  background-color: #28a745;
  border-color: #28a745;
  color: white;
}

.btn-success:hover {
  background-color: #218838;
  border-color: #1e7e34;
}

.btn-info {
  background-color: #17a2b8;
  border-color: #17a2b8;
  color: white;
}

.btn-info:hover {
  background-color: #138496;
  border-color: #117a8b;
}

.btn-warning {
  background-color: #ffc107;
  border-color: #ffc107;
  color: #000;
}

.btn-warning:hover {
  background-color: #e0a800;
  border-color: #d39e00;
}

.btn-danger {
  background-color: #e63757;
  border-color: #e63757;
  color: white;
}

.btn-danger:hover {
  background-color: #d32f4f;
  border-color: #c62828;
}
</style>
